/**
 * Constants for Submission Detail Page
 * Centralized configuration for statuses, tabs, and other shared values
 */

// Lead Status Configuration
export const STATUS_OPTIONS = [
    { value: 'submitted', label: 'Submitted', color: 'yellow', bgClass: 'bg-yellow-900/30 text-yellow-300 border-yellow-800' },
    { value: 'reviewed', label: 'Reviewed', color: 'blue', bgClass: 'bg-blue-900/30 text-blue-300 border-blue-800' },
    { value: 'contacted', label: 'Contacted', color: 'purple', bgClass: 'bg-purple-900/30 text-purple-300 border-purple-800' },
    { value: 'qualified', label: 'Qualified', color: 'indigo', bgClass: 'bg-indigo-900/30 text-indigo-300 border-indigo-800' },
    { value: 'converted', label: 'Converted', color: 'green', bgClass: 'bg-green-900/40 text-green-200 border-green-700' },
    { value: 'closed', label: 'Closed', color: 'gray', bgClass: 'bg-gray-700 text-gray-200 border-gray-500' },
    { value: 'pending', label: 'Pending', color: 'orange', bgClass: 'bg-orange-900/30 text-orange-300 border-orange-800' },
];

// Tab Configuration
export const TAB_CONFIG = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'playbook', label: 'AI Playbook', icon: '🎯' },
    { id: 'analytics', label: 'Analytics', icon: '📈' },
    { id: 'activities', label: 'Activities', icon: '⚡' },
    { id: 'timeline', label: 'Timeline', icon: '🕐' },
    { id: 'risks', label: 'Risks', icon: '⚠️' },
];

// Activity Types
export const ACTIVITY_TYPES = [
    { value: 'call', label: 'Phone Call', icon: '📞' },
    { value: 'email', label: 'Email', icon: '✉️' },
    { value: 'meeting', label: 'Meeting', icon: '🤝' },
    { value: 'follow_up', label: 'Follow Up', icon: '🔄' },
    { value: 'note', label: 'Note', icon: '📝' },
    { value: 'document_sent', label: 'Document Sent', icon: '📄' },
    { value: 'quote_sent', label: 'Quote Sent', icon: '💰' },
];

// Priority Configuration
export const PRIORITY_CONFIG = {
    high: { label: 'High', color: 'red', bgClass: 'bg-red-900/30 text-red-300 border-red-800', icon: '🔴' },
    medium: { label: 'Medium', color: 'yellow', bgClass: 'bg-yellow-900/30 text-yellow-300 border-yellow-800', icon: '🟡' },
    low: { label: 'Low', color: 'green', bgClass: 'bg-green-900/30 text-green-300 border-green-800', icon: '🟢' },
};

// Outcome Configuration
export const OUTCOME_CONFIG = {
    positive: { label: 'Positive', color: 'green', icon: '✅' },
    neutral: { label: 'Neutral', color: 'gray', icon: '➖' },
    negative: { label: 'Negative', color: 'red', icon: '❌' },
    no_response: { label: 'No Response', color: 'orange', icon: '⏳' },
};

// Country Code Mapping for Scoring Engine
export const COUNTRY_CODE_MAPPING = {
    'germany': 'DEU',
    'canada': 'CAN',
    'australia': 'AUS',
    'uk': 'GBR',
    'united kingdom': 'GBR',
    'usa': 'USA',
    'united states': 'USA',
    'new zealand': 'NZL',
    'notsure': 'DEU',
    'not sure': 'DEU',
};

// Risk Severity Configuration
export const RISK_SEVERITY_CONFIG = {
    High: { color: 'red', bgClass: 'bg-red-900/20 border-red-800', textClass: 'text-red-300', icon: '!' },
    Medium: { color: 'yellow', bgClass: 'bg-yellow-900/20 border-yellow-800', textClass: 'text-yellow-300', icon: '⚠' },
    Low: { color: 'blue', bgClass: 'bg-blue-900/20 border-blue-800', textClass: 'text-blue-300', icon: 'i' },
};

// Package Names - Map of known package keys to display names
export const PACKAGE_NAMES = {
    'essential': 'Essential Package',
    'premium': 'Premium Package',
    'diy': 'DIY Package',
    'standard': 'Standard Package',
    'vip': 'VIP Package',
    'essential-DEU': 'Essential Package - Germany',
    'premium-DEU': 'Premium Package - Germany',
};

// Known Pathway Names - These match common pathway slugs/names from the database
export const PATHWAY_NAMES = {
    'work-visa': 'Work Visa',
    'student-visa': 'Student Visa',
    'job-seeker': 'Job Seeker Visa',
    'skilled-worker': 'Skilled Worker Visa',
    'family-reunion': 'Family Reunion Visa',
    'business-visa': 'Business Visa',
    'blue-card': 'EU Blue Card',
    'apprenticeship': 'Apprenticeship Visa',
    'language-learner': 'Language Learner Visa',
    'research-visa': 'Research Visa',
    'freelance': 'Freelance Visa',
    'self-employment': 'Self-Employment Visa',
};

// Country Names - ISO codes to display names
export const COUNTRY_NAMES = {
    'deu': 'Germany 🇩🇪',
    'germany': 'Germany 🇩🇪',
    'can': 'Canada 🇨🇦',
    'canada': 'Canada 🇨🇦',
    'aus': 'Australia 🇦🇺',
    'australia': 'Australia 🇦🇺',
    'gbr': 'United Kingdom 🇬🇧',
    'uk': 'United Kingdom 🇬🇧',
    'usa': 'United States 🇺🇸',
    'nzl': 'New Zealand 🇳🇿',
};

// Get status configuration by value
export const getStatusConfig = (status) => {
    return STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
};

// Get activity type configuration by value
export const getActivityTypeConfig = (type) => {
    return ACTIVITY_TYPES.find(t => t.value === type) || { value: type, label: type, icon: '📝' };
};

// Get country code for scoring
export const getCountryCode = (countryName) => {
    if (!countryName) return 'DEU';
    return COUNTRY_CODE_MAPPING[countryName.toLowerCase()] || 'DEU';
};

// Get human-readable country name from code
export const getCountryDisplayName = (countryCode) => {
    if (!countryCode) return 'Not specified';
    const normalized = countryCode.toLowerCase();
    return COUNTRY_NAMES[normalized] || countryCode;
};

// Get package display name - handles both simple keys and compound keys with UUIDs
export const getPackageDisplayName = (packageKey) => {
    if (!packageKey) return 'No package selected';

    // Check if it's a known simple key first
    if (PACKAGE_NAMES[packageKey]) {
        return PACKAGE_NAMES[packageKey];
    }

    // Handle compound keys like "uuid-diy" or "uuid-premium"
    const parts = packageKey.split('-');
    if (parts.length > 1) {
        const suffix = parts[parts.length - 1].toLowerCase();
        if (PACKAGE_NAMES[suffix]) {
            return PACKAGE_NAMES[suffix];
        }
        // Format the suffix nicely
        return suffix.charAt(0).toUpperCase() + suffix.slice(1) + ' Package';
    }

    return packageKey;
};

// Get pathway display name - handles UUIDs by extracting slug or providing formatted name
export const getPathwayDisplayName = (pathwayValue, pathwayData = null) => {
    if (!pathwayValue) return 'Not determined';

    // If we have actual pathway data object with name
    if (pathwayData?.name) return pathwayData.name;
    if (pathwayData?.display_name) return pathwayData.display_name;

    // Check if it's a known pathway key/slug
    const normalized = pathwayValue.toLowerCase().replace(/_/g, '-');
    if (PATHWAY_NAMES[normalized]) {
        return PATHWAY_NAMES[normalized];
    }

    // Check if any known key is contained in the value (for partial matches)
    for (const [key, name] of Object.entries(PATHWAY_NAMES)) {
        if (normalized.includes(key)) {
            return name;
        }
    }

    // If it looks like a UUID, return a generic message
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (uuidRegex.test(pathwayValue)) {
        return 'Selected Pathway';
    }

    // Format camelCase or snake_case to readable text
    return pathwayValue
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, c => c.toUpperCase());
};

// Get preferred countries as formatted string
export const formatPreferredCountries = (countries) => {
    if (!countries) return 'Not specified';

    if (typeof countries === 'string') {
        return getCountryDisplayName(countries);
    }

    if (Array.isArray(countries) && countries.length > 0) {
        return countries.map(c => getCountryDisplayName(c)).join(', ');
    }

    return 'Not specified';
};

export default {
    STATUS_OPTIONS,
    TAB_CONFIG,
    ACTIVITY_TYPES,
    PRIORITY_CONFIG,
    OUTCOME_CONFIG,
    COUNTRY_CODE_MAPPING,
    RISK_SEVERITY_CONFIG,
    PACKAGE_NAMES,
    PATHWAY_NAMES,
    COUNTRY_NAMES,
    getStatusConfig,
    getActivityTypeConfig,
    getCountryCode,
    getCountryDisplayName,
    getPackageDisplayName,
    getPathwayDisplayName,
    formatPreferredCountries,
};
